// ============================================================================
// CONTACT SLA OVERRIDES PANEL
// Renders inside the StakeholderOverviewTab to manage per-customer SLA targets.
// Allows admins to set response/resolution minutes, alert windows, and at-risk
// thresholds that override the per-item-type defaults for a specific contact.
// ============================================================================

'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Target,
  Plus,
  Trash2,
  Loader2,
  Save,
  X,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';

interface SlaOverride {
  id: number;
  contact_id: number;
  item_type: string;
  item_subtype: string | null;
  enabled: boolean;
  response_minutes: number | null;
  resolution_minutes: number | null;
  breached_alert_minutes: number;
  at_risk_threshold_pct: number;
  priority_override: string | null;
  notes: string | null;
}

interface ContactSlaOverridesPanelProps {
  contactId: number;
  isDark: boolean;
}

const ITEM_TYPE_OPTIONS = [
  { value: 'job', label: 'Jobs' },
  { value: 'appointment', label: 'Appointments' },
  { value: 'task', label: 'Tasks' },
  { value: 'ticket', label: 'Tickets' },
  { value: 'project', label: 'Projects' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'critical', label: 'Critical' },
];

function toPositiveIntOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function toNonNegInt(value: string, fallback: number): number {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function toBoundedPct(value: string, fallback: number): number {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  const n = parseInt(trimmed, 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, 1), 99);
}

function formatMinutes(min: number | null | undefined): string {
  if (!min || min <= 0) return 'Default';
  if (min % 1440 === 0) return `${min / 1440}d`;
  if (min % 60 === 0) return `${min / 60}h`;
  return `${min}m`;
}

export default function ContactSlaOverridesPanel({ contactId, isDark }: ContactSlaOverridesPanelProps) {
  const [overrides, setOverrides] = useState<SlaOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    item_type: 'job',
    item_subtype: '',
    enabled: true,
    response_minutes: '',
    resolution_minutes: '',
    breached_alert_minutes: '60',
    at_risk_threshold_pct: '75',
    priority_override: '',
    notes: '',
  });

  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const res = await fetch(`/api/v1/contacts/${contactId}/sla-overrides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json?.success && Array.isArray(json.data)) {
        setOverrides(json.data as SlaOverride[]);
      }
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    void fetchOverrides();
  }, [fetchOverrides]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const payload: Record<string, unknown> = {
        item_type: formData.item_type,
        item_subtype: formData.item_subtype.trim() || null,
        enabled: formData.enabled,
        response_minutes: toPositiveIntOrNull(formData.response_minutes),
        resolution_minutes: toPositiveIntOrNull(formData.resolution_minutes),
        breached_alert_minutes: toNonNegInt(formData.breached_alert_minutes, 60),
        at_risk_threshold_pct: toBoundedPct(formData.at_risk_threshold_pct, 75),
        priority_override: formData.priority_override.trim() || null,
        notes: formData.notes.trim() || null,
      };

      if (editingId !== null) {
        payload.override_id = editingId;
      }

      const res = await fetch(`/api/v1/contacts/${contactId}/sla-overrides`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (json?.success) {
        notify.success('SLA override saved');
        setShowAddForm(false);
        setEditingId(null);
        setFormData({
          item_type: 'job',
          item_subtype: '',
          enabled: true,
          response_minutes: '',
          resolution_minutes: '',
          breached_alert_minutes: '60',
          at_risk_threshold_pct: '75',
          priority_override: '',
          notes: '',
        });
        void fetchOverrides();
      } else {
        notify.error(json?.error || 'Failed to save SLA override');
      }
    } catch {
      notify.error('Failed to save SLA override');
    } finally {
      setSaving(false);
    }
  }, [contactId, editingId, formData, fetchOverrides]);

  const handleDelete = useCallback(async (overrideId: number) => {
    if (!(await confirm({ message: 'Remove this SLA override? The customer will revert to the default SLA targets.', variant: 'danger' }))) {
      return;
    }

    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const res = await fetch(
        `/api/v1/contacts/${contactId}/sla-overrides?override_id=${overrideId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const json = await res.json();
      if (json?.success) {
        notify.success('SLA override removed');
        void fetchOverrides();
      } else {
        notify.error(json?.error || 'Failed to remove SLA override');
      }
    } catch {
      notify.error('Failed to remove SLA override');
    }
  }, [contactId, fetchOverrides]);

  const handleEdit = useCallback((override: SlaOverride) => {
    setEditingId(override.id);
    setFormData({
      item_type: override.item_type,
      item_subtype: override.item_subtype || '',
      enabled: override.enabled,
      response_minutes: override.response_minutes?.toString() || '',
      resolution_minutes: override.resolution_minutes?.toString() || '',
      breached_alert_minutes: override.breached_alert_minutes.toString(),
      at_risk_threshold_pct: override.at_risk_threshold_pct.toString(),
      priority_override: override.priority_override || '',
      notes: override.notes || '',
    });
    setShowAddForm(true);
  }, []);

  const handleCancel = useCallback(() => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({
      item_type: 'job',
      item_subtype: '',
      enabled: true,
      response_minutes: '',
      resolution_minutes: '',
      breached_alert_minutes: '60',
      at_risk_threshold_pct: '75',
      priority_override: '',
      notes: '',
    });
  }, []);

  const inputClass = `w-full rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`;
  const labelClass = `mb-1 block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h4 className={`flex items-center gap-2 font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Target className="h-4 w-4 text-violet-500" />
            SLA Overrides
          </h4>
          <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Set customer-specific SLA targets that override the per-item-type defaults.
          </p>
        </div>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${isDark ? 'bg-violet-600 text-white hover:bg-violet-500' : 'bg-violet-500 text-white hover:bg-violet-600'}`}
          >
            <Plus className="h-3.5 w-3.5" />
            Add Override
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {showAddForm && (
            <div className={`mb-4 rounded-lg border p-4 ${isDark ? 'border-gray-600 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
              <div className="mb-3 flex items-center justify-between">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {editingId !== null ? 'Edit override' : 'New SLA override'}
                </span>
                <button type="button" onClick={handleCancel} className={`rounded p-1 ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-200'}`}>
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className={labelClass}>Item type</label>
                  <select
                    value={formData.item_type}
                    onChange={(e) => setFormData((prev) => ({ ...prev, item_type: e.target.value }))}
                    className={inputClass}
                  >
                    {ITEM_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Subtype (optional)</label>
                  <input
                    type="text"
                    value={formData.item_subtype}
                    onChange={(e) => setFormData((prev) => ({ ...prev, item_subtype: e.target.value }))}
                    placeholder="e.g. electrical, plumbing"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Priority filter</label>
                  <select
                    value={formData.priority_override}
                    onChange={(e) => setFormData((prev) => ({ ...prev, priority_override: e.target.value }))}
                    className={inputClass}
                  >
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Response (mins)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.response_minutes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, response_minutes: e.target.value }))}
                    placeholder="Default"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Resolution (mins)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.resolution_minutes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, resolution_minutes: e.target.value }))}
                    placeholder="Default"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Pre-breach alert (mins)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.breached_alert_minutes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, breached_alert_minutes: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>At-risk threshold (%)</label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={formData.at_risk_threshold_pct}
                    onChange={(e) => setFormData((prev) => ({ ...prev, at_risk_threshold_pct: e.target.value }))}
                    className={inputClass}
                  />
                </div>
                <div className="flex items-end">
                  <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={formData.enabled}
                      onChange={(e) => setFormData((prev) => ({ ...prev, enabled: e.target.checked }))}
                      className="h-4 w-4 rounded"
                    />
                    Enabled
                  </label>
                </div>
              </div>

              <div className="mt-3">
                <label className={labelClass}>Notes (optional)</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g. Premium customer — tighter SLA"
                  className={inputClass}
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`rounded-lg px-3 py-2 text-sm ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium ${isDark ? 'bg-violet-600 text-white hover:bg-violet-500' : 'bg-violet-500 text-white hover:bg-violet-600'} disabled:opacity-50`}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {editingId !== null ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {overrides.length === 0 && !showAddForm ? (
            <div className={`rounded-lg border border-dashed p-4 text-center ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
              <Target className="mx-auto mb-2 h-6 w-6 opacity-40" />
              <p className="text-sm">No SLA overrides configured for this customer.</p>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                This customer uses the default SLA targets from the item-type configuration.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {overrides.map((override) => (
                <div
                  key={override.id}
                  className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {ITEM_TYPE_OPTIONS.find((o) => o.value === override.item_type)?.label || override.item_type}
                      </span>
                      {override.item_subtype && (
                        <span className={`rounded px-1.5 py-0.5 text-xs ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                          {override.item_subtype}
                        </span>
                      )}
                      {override.priority_override && (
                        <span className={`rounded px-1.5 py-0.5 text-xs ${isDark ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                          {override.priority_override}
                        </span>
                      )}
                      {override.enabled ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                      )}
                    </div>
                    <div className={`mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>Response: <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>{formatMinutes(override.response_minutes)}</strong></span>
                      <span>Resolution: <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>{formatMinutes(override.resolution_minutes)}</strong></span>
                      <span>Alert: <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>{override.breached_alert_minutes}m</strong></span>
                      <span>At-risk: <strong className={isDark ? 'text-gray-300' : 'text-gray-700'}>{override.at_risk_threshold_pct}%</strong></span>
                    </div>
                    {override.notes && (
                      <p className={`mt-1 text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{override.notes}</p>
                    )}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleEdit(override)}
                      className={`rounded p-1.5 text-xs ${isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                      <Save className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(override.id)}
                      className={`rounded p-1.5 ${isDark ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
